import { getSysConfigApi } from '@/api/basicService/systemSetting/systemConfig'
import { getFileListApi } from '@/api/publicAxios/publicAxios'
import { useAppStoreWithOut } from '@/store/modules/app'

const BRAND_INIT_TIMEOUT_MS = 5000
/** 本地缓存：刷新瞬间即可还原系统名称与 Logo，避免加载页闪默认品牌 */
export const APP_BRAND_CACHE_KEY = 'app_brand_cache'
/** 系统未配置或附件预览失败时的默认品牌图（交通集团） */
export const DEFAULT_BRAND_LOGO_URL = '/default-brand-logo.png'

export type AppBrandCache = {
  name?: string
  /** 系统配置 Logo 附件 fileId（subKind=sysConfigIcon） */
  logoFileId?: string
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  return Promise.race([
    promise,
    new Promise<undefined>((resolve) => {
      setTimeout(() => resolve(undefined), ms)
    })
  ])
}

/** 附件预览直链：匿名可访问，可作 img/favicon 的 src（无需 blob） */
export function buildAttachmentPreviewUrl(fileId: string): string {
  return `/api/common/attachment/preview?fileId=${encodeURIComponent(fileId)}`
}

export function readBrandCache(): AppBrandCache | null {
  try {
    const raw = localStorage.getItem(APP_BRAND_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as AppBrandCache
    if (!parsed || typeof parsed !== 'object') return null
    return parsed
  } catch {
    return null
  }
}

export function writeBrandCache(cache: AppBrandCache) {
  try {
    localStorage.setItem(APP_BRAND_CACHE_KEY, JSON.stringify(cache))
  } catch {
    /* 隐私模式等写入失败时忽略 */
  }
}

function bindLogoErrorFallback(img: HTMLImageElement) {
  img.onerror = () => {
    img.onerror = null
    img.src = DEFAULT_BRAND_LOGO_URL
    img.style.display = ''
    img.removeAttribute('hidden')
    // 预览失败时清掉坏 fileId，避免下次刷新继续裂图
    try {
      const cache = readBrandCache() || {}
      if (cache.logoFileId) {
        delete cache.logoFileId
        writeBrandCache(cache)
      }
    } catch {
      /* ignore */
    }
  }
}

function setFavicon(url?: string) {
  const link =
    (document.getElementById('app-favicon') as HTMLLinkElement | null) ||
    (document.querySelector('link[rel="icon"]') as HTMLLinkElement | null)
  if (!link) return
  link.href = url || DEFAULT_BRAND_LOGO_URL
}

function setLoadingLogo(url?: string) {
  const img = document.getElementById('app-loading-logo') as HTMLImageElement | null
  if (!img) return
  const next = url || DEFAULT_BRAND_LOGO_URL
  bindLogoErrorFallback(img)
  img.src = next
  img.style.display = ''
  img.removeAttribute('hidden')
}

function setAppTitle(name: string) {
  if (!name) return
  const titleEl = document.getElementById('app-loading-title')
  if (titleEl) {
    titleEl.textContent = name
  }
  // 同步到 appStore，供 useTitle 生成标签页标题（系统名称 - 页面名）
  try {
    useAppStoreWithOut().setTitle(name)
  } catch {
    /* Pinia 尚未挂载时忽略，挂载后 initAppBrand 会再写一次 */
  }
  document.title = name
}

/** 将品牌应用到启动页 DOM（可在挂载前多次调用） */
export function applyBrandToDom(brand: { name?: string; logoUrl?: string }) {
  if (brand.name) {
    setAppTitle(brand.name)
  }
  setFavicon(brand.logoUrl || DEFAULT_BRAND_LOGO_URL)
  setLoadingLogo(brand.logoUrl || DEFAULT_BRAND_LOGO_URL)
}

/** 启动最早阶段：用本地缓存立刻替换默认 logo / 文案 */
export function applyCachedBrand() {
  const cache = readBrandCache()
  if (!cache) {
    setFavicon(DEFAULT_BRAND_LOGO_URL)
    setLoadingLogo(DEFAULT_BRAND_LOGO_URL)
    return
  }
  applyBrandToDom({
    name: cache.name,
    logoUrl: cache.logoFileId ? buildAttachmentPreviewUrl(cache.logoFileId) : DEFAULT_BRAND_LOGO_URL
  })
}

/**
 * 启动页 favicon / Logo / 标题：与侧边栏同源（系统配置 name + sysConfigIcon）
 * 成功后写入 localStorage，下次刷新可在 JS 包加载前由 index.html 内联脚本还原
 */
export async function initAppBrand() {
  // 先应用缓存，缩短默认品牌可见时间
  applyCachedBrand()

  try {
    const res = await withTimeout(getSysConfigApi(), BRAND_INIT_TIMEOUT_MS)
    const data = res?.data
    if (!data) return

    const cache: AppBrandCache = { ...readBrandCache() }
    if (data.name) {
      cache.name = data.name
      setAppTitle(data.name)
    }

    if (!data.bizFileId) {
      delete cache.logoFileId
      writeBrandCache(cache)
      setFavicon(DEFAULT_BRAND_LOGO_URL)
      setLoadingLogo(DEFAULT_BRAND_LOGO_URL)
      window.dispatchEvent(new CustomEvent('app-brand-updated'))
      return
    }

    const imgList = await withTimeout(getFileListApi(data.bizFileId, 'sysConfig'), BRAND_INIT_TIMEOUT_MS)
    if (!imgList || imgList?.code !== 0 || !Array.isArray(imgList.data)) {
      writeBrandCache(cache)
      window.dispatchEvent(new CustomEvent('app-brand-updated'))
      return
    }

    const iconFile = imgList.data.find((item: { subKind?: string }) => item.subKind === 'sysConfigIcon')
    if (!iconFile?.fileId) {
      delete cache.logoFileId
      writeBrandCache(cache)
      setFavicon(DEFAULT_BRAND_LOGO_URL)
      setLoadingLogo(DEFAULT_BRAND_LOGO_URL)
      window.dispatchEvent(new CustomEvent('app-brand-updated'))
      return
    }

    cache.logoFileId = iconFile.fileId
    const logoUrl = buildAttachmentPreviewUrl(iconFile.fileId)
    setFavicon(logoUrl)
    setLoadingLogo(logoUrl)
    writeBrandCache(cache)
    window.dispatchEvent(new CustomEvent('app-brand-updated'))
  } catch {
    const cache = readBrandCache()
    if (cache?.logoFileId) {
      const logoUrl = buildAttachmentPreviewUrl(cache.logoFileId)
      setFavicon(logoUrl)
      setLoadingLogo(logoUrl)
    } else {
      setFavicon(DEFAULT_BRAND_LOGO_URL)
      setLoadingLogo(DEFAULT_BRAND_LOGO_URL)
    }
    if (cache?.name) {
      setAppTitle(cache.name)
    }
  }
}
